/**
 * Dialog para edição de contato
 *
 * Permite editar nome, telefone (com seletor de código de país) e email
 * Avatar é gerenciado automaticamente pelo webhook do WhatsApp
 *
 * @version 2.0.0
 * RELEASE: 2026-08-28 v2.0.0 - Edição 100% via RPC `update_contact` (v1.0.0).
 *          Remove o UPDATE direto na tabela contacts e o SELECT de tenant_id;
 *          a validação de duplicidade de telefone/email passa a ser server-side.
 */


import { useState, useEffect } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { normalizePhoneIdentifier } from '@/lib/phone-utils';
import { toast } from 'sonner';
import { SellerSelector } from '@/components/crm/SellerSelector';


// Schema de validação para edição de contato (avatar é gerenciado automaticamente)
const contactSchema = z.object({
  name: z.string().trim().min(1, 'Nome é obrigatório').max(255, 'Nome muito longo'),
  phone: z.string().trim().max(50, 'Telefone muito longo').optional().or(z.literal('')),
  email: z.string().trim().email('Email inválido').max(255, 'Email muito longo').optional().or(z.literal('')),
  seller_id: z.string().nullable().optional(),
}).refine(data => data.phone || data.email, {
  message: 'Informe pelo menos um telefone ou email',
  path: ['phone'],
});

type ContactFormData = z.infer<typeof contactSchema>;

interface Contact {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  avatar_url: string | null;
  seller_id?: string | null;
}

interface EditContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: Contact | null;
  onSuccess: () => void;
}

export function EditContactDialog({ 
  open, 
  onOpenChange, 
  contact,
  onSuccess 
}: EditContactDialogProps) {
  const [loading, setLoading] = useState(false);
  

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      seller_id: null,
    },
  });

  // Popula o formulário quando o contato muda
  useEffect(() => {
    if (contact && open) {
      form.reset({
        name: contact.name || '',
        phone: contact.phone || '',
        email: contact.email || '',
        seller_id: contact.seller_id || null,
      });
    }
  }, [contact, open, form]);

  const onSubmit = async (data: ContactFormData) => {
    if (!contact) return;

    setLoading(true);

    try {
      // v2.0.0 — atualização 100% via RPC. A própria RPC valida tenant,
      // propriedade do contato, normaliza o telefone e bloqueia duplicidade
      // de telefone/email contra outros contatos do mesmo tenant.
      const { error } = await supabase.rpc('update_contact' as any, {
        p_contact_id: contact.id,
        p_name: data.name,
        p_phone: normalizePhoneIdentifier(data.phone) || null,
        p_email: data.email || null,
        p_seller_id: data.seller_id || null,
      } as any);

      if (error) {
        // 23505 = unique_violation levantada pela RPC (telefone/email duplicado)
        if (error.code === '23505') {
          toast.error(error.message || 'Já existe um contato com este telefone', { duration: 5000 });
          return;
        }
        throw error;
      }

      toast.success('Contato atualizado com sucesso');
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Erro ao atualizar contato');
    } finally {
      setLoading(false);
    }
  };


  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Editar Contato</DialogTitle>
          <DialogDescription>
            Atualize as informações do contato.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input placeholder="Nome do contato" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <PhoneInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="11 99999-9999"
                      disabled={loading}
                    />
                  </FormControl>
                  <FormDescription>
                    Digite DDD + número (ex: 11 99999-9999)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="email@exemplo.com" type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="seller_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendedor</FormLabel>
                  <FormControl>
                    <SellerSelector
                      value={field.value}
                      onSelect={field.onChange}
                      placeholder="Selecione um vendedor..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => handleOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar Alterações
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
